import React from "react";
import {Card, CardBody, CardHeader, Col, Row} from 'reactstrap';
import {ApexOptions} from 'apexcharts';
import ReactApexChart from "react-apexcharts";
import '../../../i18/config';
import {useTranslation} from "react-i18next";
import {format} from "date-fns";

import {StatPeerChannelDataType} from "../../../redux/Stat/PeerChannel/types";
import {convertToMomentLocale} from "../../../helpers/functions";
import {alignDailySeries, hasAnyValue, StatDailyPoint} from "../../../helpers/statSeries";

interface InputProps {
    items: [] | Array<StatPeerChannelDataType>;
}

export const FOLLOWER_GAIN_KEYS = ['followerGainOrganic', 'followerGainPaid'];
export const PAGE_VIEW_KEYS = ['pageViews', 'uniquePageViews'];

interface AudienceChartProps {
    title: string;
    items: StatDailyPoint[];
    keys: string[];
    chartType: 'bar' | 'line';
    stacked?: boolean;
    testId: string;
}

// A per-day chart of flow metrics (they already belong to their day). Days without a
// value are gaps; a page with no value at all in the period shows "no data yet".
const AudienceChart = ({title, items, keys, chartType, stacked = false, testId}: AudienceChartProps) => {

    const {t, i18n} = useTranslation(['stat']);

    const aligned = alignDailySeries(items, keys);
    const series = aligned.series.map((serie) => ({name: t('stat:' + serie.key), data: serie.data}));

    const options: ApexOptions = {
        chart: {
            type: chartType,
            stacked: stacked,
            toolbar: {show: false},
        },
        stroke: chartType === 'line' ? {curve: 'smooth', width: 3} : {},
        dataLabels: {enabled: false},
        xaxis: {
            categories: aligned.labels,
            axisTicks: {show: false},
            axisBorder: {show: false},
            labels: {
                formatter: function (value) {
                    return format(new Date(value), 'dd MMM', {locale: convertToMomentLocale(i18n.language)});
                }
            }
        },
        grid: {
            show: true,
            xaxis: {lines: {show: true}},
            yaxis: {lines: {show: false}},
        },
        legend: {
            show: true,
            horizontalAlign: 'center',
        },
        plotOptions: {
            bar: {columnWidth: '50%'}
        },
        tooltip: {shared: true},
    };

    return (
        <Card className="card-height-100" data-testid={testId}>
            <CardHeader className="d-flex align-items-center">
                <h4 className="card-title flex-grow-1 mb-0">{title}</h4>
            </CardHeader>
            <CardBody>
                {series.length === 0 ?
                    <div className="text-muted text-center py-5" data-testid={testId + '-no-data'}>{t('stat:noDataYet')}</div>
                    :
                    <ReactApexChart
                        options={options}
                        series={series}
                        type={chartType}
                        height="320"
                        className="apex-charts"
                    />
                }
            </CardBody>
        </Card>
    );
};

/** Whether the period holds any follower-gain or page-view value. */
export const hasAudienceData = (items: StatDailyPoint[] | null | undefined): boolean =>
    hasAnyValue(items, [...FOLLOWER_GAIN_KEYS, ...PAGE_VIEW_KEYS]);

const ChannelAudienceCharts = ({items}: InputProps) => {

    const {t} = useTranslation(['stat']);
    const points = (items || []) as StatDailyPoint[];

    return (
        <Row>
            <Col xl={6}>
                <AudienceChart
                    title={t('stat:followerGains')}
                    items={points}
                    keys={FOLLOWER_GAIN_KEYS}
                    chartType="bar"
                    stacked
                    testId="channel-follower-gains"
                />
            </Col>
            <Col xl={6}>
                <AudienceChart
                    title={t('stat:pageVisitors')}
                    items={points}
                    keys={PAGE_VIEW_KEYS}
                    chartType="line"
                    testId="channel-page-views"
                />
            </Col>
        </Row>
    );
};

export default ChannelAudienceCharts;
